/**
 * 材料の原料と歩留まり(#216)の入力行。「マニュアルを編集」とレビュー画面の材料表(#222)で共有する。
 * 行は文字列のまま持つ(入力途中の「60.」を保つ)。保存形への変換は呼び出し側が
 * normalizeRawMaterials で行う。
 */
import { useI18n } from "../lib/i18n";
import { EMPTY_ROW, rawAmountOf, validQuantity, validYield, type RawMaterialRow } from "../lib/raw-materials";

interface Props {
  rows: RawMaterialRow[];
  onChange: (rows: RawMaterialRow[]) => void;
  /** 親の材料の分量・単位(必要量の目安の算出に使う) */
  parent: { quantity: number | null; unit: string | null };
}

export function RawMaterialRows({ rows, onChange, parent }: Props) {
  const { t } = useI18n();
  const update = (i: number, patch: Partial<RawMaterialRow>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <>
      {rows.map((r, i) => {
        const amount = rawAmountOf(parent, rows, r);
        return (
          <div className="raw-row" key={i}>
            <div className="edit-row raw-grid">
              <input
                type="text"
                value={r.name}
                aria-label={t("原料名")}
                placeholder={t("原料名（例: アオリイカ）")}
                onChange={(e) => update(i, { name: e.target.value })}
              />
              <input
                type="text"
                inputMode="decimal"
                value={r.yield_pct}
                aria-label={t("歩留まり（%）")}
                placeholder={t("歩留まり%（例: 60）")}
                className={validYield(r.yield_pct) ? undefined : "invalid"}
                onChange={(e) => update(i, { yield_pct: e.target.value })}
              />
              <button className="link" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                {t("この原料を外す")}
              </button>
            </div>
            <div className="edit-row raw-grid">
              <input
                type="text"
                inputMode="decimal"
                value={r.quantity}
                aria-label={t("原料の量（任意）")}
                placeholder={t("原料の量（任意）")}
                className={validQuantity(r.quantity) ? undefined : "invalid"}
                onChange={(e) => update(i, { quantity: e.target.value })}
              />
              <input
                type="text"
                value={r.unit}
                aria-label={t("単位")}
                placeholder={t("単位（例: g）")}
                onChange={(e) => update(i, { unit: e.target.value })}
              />
              <input
                type="text"
                value={r.note}
                aria-label={t("原料の備考")}
                placeholder={t("備考（例: 皮・内臓を除く）")}
                onChange={(e) => update(i, { note: e.target.value })}
              />
            </div>
            {!validYield(r.yield_pct) && <p className="error small">{t("歩留まりは 1〜100 の数値で書いてください。")}</p>}
            {!validQuantity(r.quantity) && <p className="error small">{t("原料の量は数値で書いてください。")}</p>}
            {amount && r.name.trim() && (
              <p className="muted small">
                {amount.derived
                  ? t("必要な原料の目安: {amount}（{qty} ÷ 歩留まり {yield}%）", {
                      amount: `${amount.quantity}${amount.unit ?? ""}`,
                      qty: `${parent.quantity}${parent.unit ?? ""}`,
                      yield: r.yield_pct.trim().replace(/[%％]/g, ""),
                    })
                  : t("原料の量: {amount}", { amount: `${amount.quantity}${amount.unit ?? ""}` })}
              </p>
            )}
          </div>
        );
      })}
      <button className="link" onClick={() => onChange([...rows, { ...EMPTY_ROW }])}>
        {t("＋ 原料を追加")}
      </button>
    </>
  );
}
